import React, { useMemo, useState } from "react";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../../state";
import { Line } from "react-chartjs-2";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  LegendItem,
  ChartDataset,
} from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";
import annotationPlugin from "chartjs-plugin-annotation";
import { isEqual } from "lodash";
import { numericalString } from "../../../types";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  zoomPlugin,
  annotationPlugin,
);

// Set global font defaults
ChartJS.defaults.font.family = "JaguarExuberant, Helvetica, Arial, sans-serif";

function ReplayRaceTrace() {
  const [viewMode, setViewMode] = useState<"leader" | "ghost">("ghost");

  const v2Merged = useSelector(
    (state: RootState) => state.replay.v2Merged,
    isEqual,
  );

  const processedSessionData = useSelector(
    (state: RootState) => state.replay.processedSessionData,
    isEqual,
  );

  const loadingSessionEventData = useSelector(
    (state: RootState) => state.replay.loadingSessionEventData,
  );

  const driverInfo = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.driverInfo,
    isEqual,
  );

  const processedData = useMemo(() => {
    if (!v2Merged || !processedSessionData) {
      return {
        leaderDatasets: [],
        ghostDatasets: [],
      };
    }

    // Only run on RACE
    const shortName = v2Merged?.timing?.session?.info?.shortName;
    if (shortName !== "RC") {
      return {
        leaderDatasets: [],
        ghostDatasets: [],
      };
    }

    // Figure out the average time per loop of the eventual winner
    // Because of the race tool not sending out/in laps as invalid this is necessary (have tried to fix it)
    const leader = v2Merged?.timing?.session?.standings?.overall?.active?.["1"]
      .participant as numericalString;
    const leaderLaps = v2Merged?.timing?.analysis?.laps?.[leader]?.laps;

    // First pass: calculate total lap times to find the fastest lap
    const lapTotalTimes: { lapKey: string; totalTime: number }[] = [];
    for (const [lapKey, lapData] of Object.entries(leaderLaps || {})) {
      const loopTimes = lapData?.loopSectors;
      if (!lapData?.isValid || !loopTimes) continue;

      let lapTotal = 0;
      let prevCumulativeTime = 0;
      for (const loop of Object.keys(loopTimes)) {
        const cumulativeTime = loopTimes[loop as numericalString]?.time;
        if (cumulativeTime === undefined) continue;
        lapTotal += cumulativeTime - prevCumulativeTime;
        prevCumulativeTime = cumulativeTime;
      }
      if (lapTotal > 0) {
        lapTotalTimes.push({ lapKey, totalTime: lapTotal });
      }
    }

    // Find fastest lap and filter to only include laps within 110% of fastest
    const fastestLapTime = Math.min(...lapTotalTimes.map((l) => l.totalTime));
    const threshold = fastestLapTime * 1.1; // 110% of fastest
    const validLapKeys = new Set(
      lapTotalTimes
        .filter((l) => l.totalTime <= threshold)
        .map((l) => l.lapKey),
    );

    // Second pass: calculate average loop times using only valid (fast) laps
    const loopTimeAccumulator: Record<
      string,
      { total: number; count: number }
    > = {};

    for (const [lapKey, lapData] of Object.entries(leaderLaps || {})) {
      const loopTimes = lapData?.loopSectors;
      if (!lapData?.isValid || !loopTimes) continue;
      if (!validLapKeys.has(lapKey)) continue; // Skip slow laps

      let prevCumulativeTime = 0;
      for (const loop of Object.keys(loopTimes)) {
        const cumulativeTime = loopTimes[loop as numericalString]?.time;
        if (cumulativeTime === undefined) continue;
        const perLoopTime = cumulativeTime - prevCumulativeTime;
        loopTimeAccumulator[loop] ??= { total: 0, count: 0 };
        loopTimeAccumulator[loop].total += perLoopTime;
        loopTimeAccumulator[loop].count += 1;
        prevCumulativeTime = cumulativeTime;
      }
    }

    // Calculate average lap and loop time
    const leaderAvgLoopTimes: Record<string, number> = {};
    let leaderAvgLapTime = 0;
    for (const [loop, { total, count }] of Object.entries(
      loopTimeAccumulator,
    )) {
      const avgTime = total / count;
      leaderAvgLoopTimes[loop] = avgTime;
      leaderAvgLapTime += avgTime;
    }

    // Figure out the proportion for each loop time compared to the total average time for the leader
    const loopProportions: Record<string, number> = {};
    let cumulativeProportion = 0;
    for (const [loop, avgTime] of Object.entries(leaderAvgLoopTimes)) {
      cumulativeProportion += avgTime / leaderAvgLapTime;
      loopProportions[loop] = cumulativeProportion;
    }

    // Process each driver's laps and loops to create datasets for the chart
    const timesByLapLoopDriver: Record<
      string,
      Record<
        string,
        Record<
          string,
          {
            loopTime: number;
            totalTime: number;
          }
        >
      >
    > = {};

    const timingAnalyisLaps = v2Merged?.timing?.analysis?.laps;
    for (const [driver, driverLaps] of Object.entries(
      timingAnalyisLaps || {},
    )) {
      let driverTotalTime = 0;
      for (const [lap, lapData] of Object.entries(driverLaps?.laps || {})) {
        let prevCumulativeTime = 0;
        for (const [loop, loopData] of Object.entries(
          lapData?.loopSectors || {},
        )) {
          const cumulativeTime = loopData?.time;
          if (cumulativeTime === undefined) continue;
          const perLoopTime = cumulativeTime - prevCumulativeTime;
          driverTotalTime += perLoopTime;
          prevCumulativeTime = cumulativeTime;
          timesByLapLoopDriver[lap] ??= {};
          timesByLapLoopDriver[lap][loop] ??= {};
          timesByLapLoopDriver[lap][loop][driver] = {
            loopTime: perLoopTime,
            totalTime: driverTotalTime,
          };
        }
      }
    }

    const ghostDatasetByDriver: Record<string, ChartDataset<"line">> = {};
    const ghostAttackDatasetsByDriver: Record<string, ChartDataset<"line">[]> =
      {};
    const ghostDatasetAttackActivationByDriver: Record<
      string,
      ChartDataset<"line">[]
    > = {};

    const leaderDatasetByDriver: Record<string, ChartDataset<"line">> = {};
    const leaderAttackDatasetsByDriver: Record<string, ChartDataset<"line">[]> =
      {};
    const leaderDatasetAttackActivationByDriver: Record<
      string,
      ChartDataset<"line">[]
    > = {};

    // Figure out the deltas to leader/ghost for each lap and loop for each driver
    let ghostTotalTime = 0;
    const previousAttackStatusByDriver: Record<string, boolean> = {};
    const currentLeaderAttackDatasetByDriver: Record<
      string,
      ChartDataset<"line">
    > = {};
    const currentGhostAttackDatasetByDriver: Record<
      string,
      ChartDataset<"line">
    > = {};

    // Create set of caution lap numbers for quick lookup
    const cautionLapNumbers = new Set(
      (processedSessionData.cautionLaps || []).map((cl) => cl.lap),
    );

    // Get the first lap number
    const lapNumbers = Object.keys(timesByLapLoopDriver).map(Number);
    const firstLap = lapNumbers.length > 0 ? Math.min(...lapNumbers) : null;

    for (const [lap, loops] of Object.entries(timesByLapLoopDriver)) {
      const lapNum = Number(lap);
      const isCautionLap = cautionLapNumbers.has(lapNum);
      const isFirstLap = lapNum === firstLap;

      for (const [loop, drivers] of Object.entries(loops)) {
        const currentLeaderData = Object.values(drivers).reduce((min, entry) =>
          entry.totalTime < min.totalTime ? entry : min,
        );
        const currentLeaderTotalTime = currentLeaderData.totalTime;

        // For caution laps or first lap, use actual leader loop time; otherwise use average
        if (isCautionLap || isFirstLap) {
          ghostTotalTime += currentLeaderData.loopTime;
        } else {
          ghostTotalTime += leaderAvgLoopTimes[loop];
        }

        for (const [driver, { totalTime }] of Object.entries(drivers)) {
          const xValue = Number(lap) + loopProportions[loop];

          const deltaToLeader = (currentLeaderTotalTime - totalTime) / 1000;
          const deltaToGhost = (ghostTotalTime - totalTime) / 1000;

          // Initialize leader dataset for this driver if not exists
          if (!leaderDatasetByDriver[driver]) {
            const driverName =
              processedSessionData.driverNamesByNumber[
                driver as numericalString
              ] || driver;
            const driverInfoEntry =
              driverInfo[driver as keyof typeof driverInfo];
            const color = driverInfoEntry?.driverTeamColour || "white";
            const lineStyle = driverInfoEntry?.driverLineStyle || "solid";
            const borderDash = lineStyle === "solid" ? [] : [10, 5];

            leaderDatasetByDriver[driver] = {
              label: driverName,
              data: [],
              backgroundColor: color,
              borderColor: color,
              borderWidth: 2,
              pointRadius: 0,
              pointHoverRadius: 0,
              fill: false,
              borderDash: borderDash,
              order: 2,
            };

            ghostDatasetByDriver[driver] = {
              label: driverName,
              data: [],
              backgroundColor: color,
              borderColor: color,
              borderWidth: 2,
              pointRadius: 0,
              pointHoverRadius: 0,
              fill: false,
              borderDash: borderDash,
              order: 2,
            };
          }
          (
            leaderDatasetByDriver[driver].data as { x: number; y: number }[]
          ).push({ x: xValue, y: deltaToLeader });
          (
            ghostDatasetByDriver[driver].data as { x: number; y: number }[]
          ).push({ x: xValue, y: deltaToGhost });

          const previousAttackStatus =
            previousAttackStatusByDriver[driver] ?? false;
          const attackStatus =
            processedSessionData.attackStatusByDriverLapLoop[
              driver as numericalString
            ]?.[Number(lap)]?.[Number(loop)] ?? false;

          // If attack just started, add activation marker
          if (!previousAttackStatus && attackStatus) {
            const driverInfoEntry =
              driverInfo[driver as keyof typeof driverInfo];
            const color = driverInfoEntry?.driverTeamColour || "white";

            leaderDatasetAttackActivationByDriver[driver] ??= [];
            leaderDatasetAttackActivationByDriver[driver].push({
              label: "",
              data: [{ x: xValue, y: deltaToLeader }],
              backgroundColor: color,
              borderColor: color,
              borderWidth: 2,
              pointRadius: 4,
              pointStyle: "circle",
              showLine: false,
              order: 0,
            });

            ghostDatasetAttackActivationByDriver[driver] ??= [];
            ghostDatasetAttackActivationByDriver[driver].push({
              label: "",
              data: [{ x: xValue, y: deltaToGhost }],
              backgroundColor: color,
              borderColor: color,
              borderWidth: 2,
              pointRadius: 4,
              pointStyle: "circle",
              showLine: false,
              order: 0,
            });
          }

          // If attack just ended, bank the current attack datasets
          if (previousAttackStatus && !attackStatus) {
            if (currentLeaderAttackDatasetByDriver[driver]) {
              leaderAttackDatasetsByDriver[driver] ??= [];
              leaderAttackDatasetsByDriver[driver].push(
                currentLeaderAttackDatasetByDriver[driver],
              );
              delete currentLeaderAttackDatasetByDriver[driver];
            }
            if (currentGhostAttackDatasetByDriver[driver]) {
              ghostAttackDatasetsByDriver[driver] ??= [];
              ghostAttackDatasetsByDriver[driver].push(
                currentGhostAttackDatasetByDriver[driver],
              );
              delete currentGhostAttackDatasetByDriver[driver];
            }
          }

          // If in attack mode, add to current attack datasets
          if (attackStatus) {
            if (!currentLeaderAttackDatasetByDriver[driver]) {
              const driverName =
                processedSessionData.driverNamesByNumber[
                  driver as numericalString
                ] || driver;
              const driverInfoEntry =
                driverInfo[driver as keyof typeof driverInfo];
              const color = driverInfoEntry?.driverTeamColour || "white";

              currentLeaderAttackDatasetByDriver[driver] = {
                label: `${driverName} (Attack)`,
                data: [],
                backgroundColor: color,
                borderColor: color,
                borderWidth: 6,
                pointRadius: 0,
                pointHoverRadius: 0,
                fill: false,
                borderDash: [],
                order: 1,
              };

              currentGhostAttackDatasetByDriver[driver] = {
                label: `${driverName} (Attack)`,
                data: [],
                backgroundColor: color,
                borderColor: color,
                borderWidth: 6,
                pointRadius: 0,
                pointHoverRadius: 0,
                fill: false,
                borderDash: [],
                order: 1,
              };
            }
            (
              currentLeaderAttackDatasetByDriver[driver].data as {
                x: number;
                y: number;
              }[]
            ).push({ x: xValue, y: deltaToLeader });
            (
              currentGhostAttackDatasetByDriver[driver].data as {
                x: number;
                y: number;
              }[]
            ).push({ x: xValue, y: deltaToGhost });
          }

          previousAttackStatusByDriver[driver] = attackStatus;

          //
        }
      }
    }

    // Bank any remaining active attack datasets
    for (const [driver, attackDataset] of Object.entries(
      currentLeaderAttackDatasetByDriver,
    )) {
      leaderAttackDatasetsByDriver[driver] ??= [];
      leaderAttackDatasetsByDriver[driver].push(attackDataset);
    }
    for (const [driver, attackDataset] of Object.entries(
      currentGhostAttackDatasetByDriver,
    )) {
      ghostAttackDatasetsByDriver[driver] ??= [];
      ghostAttackDatasetsByDriver[driver].push(attackDataset);
    }

    // Combine all datasets
    return {
      leaderDatasets: [
        ...Object.values(leaderDatasetByDriver),
        ...Object.values(leaderAttackDatasetsByDriver).flat(),
        ...Object.values(leaderDatasetAttackActivationByDriver).flat(),
      ],
      ghostDatasets: [
        ...Object.values(ghostDatasetByDriver),
        ...Object.values(ghostAttackDatasetsByDriver).flat(),
        ...Object.values(ghostDatasetAttackActivationByDriver).flat(),
      ],
      cautionLapNumbers,
    };
  }, [v2Merged, processedSessionData, driverInfo]);

  // Build caution lap annotations
  const cautionAnnotations = useMemo(() => {
    const cautionLaps = processedSessionData?.cautionLaps || [];
    if (cautionLaps.length === 0) return {};

    const colorMap: Record<string, string> = {
      FULL_YELLOW: "rgba(255, 255, 0, 0.3)",
      SAFETY_CAR: "rgba(255, 165, 0, 0.3)",
      RED: "rgba(255, 0, 0, 0.3)",
    };

    const annotations: Record<string, object> = {};
    cautionLaps.forEach((caution, idx) => {
      annotations[`caution-${idx}`] = {
        type: "box",
        xMin: caution.lap,
        xMax: caution.lap + 1,
        backgroundColor: colorMap[caution.type] || "rgba(128, 128, 128, 0.3)",
        borderWidth: 0,
        drawTime: "beforeDatasetsDraw",
      };
    });

    return annotations;
  }, [processedSessionData?.cautionLaps]);

  const chartData = {
    labels: [],
    datasets:
      viewMode === "leader"
        ? processedData.leaderDatasets
        : processedData.ghostDatasets,
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false as const,
    plugins: {
      tooltip: {
        mode: "nearest" as const,
        intersect: true,
        filter: (tooltipItem: { dataset: { label?: string } }) => {
          const label = tooltipItem.dataset.label;
          return !!label && !label.includes("(Attack)");
        },
        callbacks: {
          title: () => "",
          label: (context: {
            dataset: { label?: string };
            parsed: { y: number };
          }) => {
            const driverName = context.dataset.label || "";
            const delta = context.parsed.y.toFixed(3);
            return `${driverName}: ${delta}s`;
          },
        },
      },
      datalabels: {
        display: false,
      },
      legend: {
        display: true,
        position: "top" as const,
        // onClick: () => {},
        labels: {
          color: "#000000ff",
          usePointStyle: true,
          pointStyle: "line" as const,
          filter: (legendItem: LegendItem) => {
            // Only show labels that are not empty and not attack labels
            return (
              legendItem.text !== "" && !legendItem.text?.includes("(Attack)")
            );
          },
          generateLabels: (chart: ChartJS): LegendItem[] => {
            const datasets = chart.data.datasets;
            return datasets.map((dataset, index) => ({
              text: dataset.label ?? "",
              strokeStyle: dataset.borderColor as string,
              lineWidth: (dataset.borderWidth as number) ?? 3,
              lineDash: (dataset as { borderDash?: number[] }).borderDash ?? [],
              hidden: !chart.isDatasetVisible(index),
              datasetIndex: index,
              pointStyle: "line" as const,
            }));
          },
        },
      },
      title: {
        display: true,
        text:
          viewMode === "leader"
            ? "Race Trace - Delta to Leader"
            : "Race Trace - Delta to Ghost",
        color: "#000000ff",
        font: {
          size: 18,
          weight: "bold" as const,
        },
      },
      annotation: {
        annotations: cautionAnnotations,
      },
    },
    scales: {
      x: {
        type: "linear" as const,
        title: {
          display: true,
          text: "Lap",
          color: "#000000ff",
        },
        ticks: {
          color: "#000000ff",
          stepSize: 1,
        },
        grid: {
          color: "rgba(0, 0, 0, 0.1)",
        },
      },
      y: {
        title: {
          display: true,
          text:
            viewMode === "leader"
              ? "Delta to Leader (s)"
              : "Delta to Ghost (s)",
          color: "#000000ff",
        },
        ticks: {
          color: "#000000ff",
        },
        grid: {
          color: "rgba(0, 0, 0, 0.1)",
        },
      },
    },
    interaction: {
      mode: undefined,
    },
    hover: {
      mode: undefined,
    },
    elements: {
      point: {
        backgroundColor: "#000000ff",
        borderWidth: 2,
      },
    },
  };

  const hasRaceData = processedData.leaderDatasets.length > 0;

  return (
    <div className={styles.container}>
      <div className={styles.controlsBar}>
        <select
          value={viewMode}
          onChange={(e) => setViewMode(e.target.value as "leader" | "ghost")}
          className={styles.viewModeSelect}
          disabled={loadingSessionEventData}
        >
          <option value="leader">Delta to Leader</option>
          <option value="ghost">Delta to Ghost</option>
        </select>
      </div>
      {loadingSessionEventData ? (
        <div className={styles.loadingContainer}>
          <div className={styles.spinner} />
        </div>
      ) : hasRaceData ? (
        <div className={styles.chartWrapper}>
          <Line data={chartData} options={chartOptions} />
        </div>
      ) : (
        <div className={styles.emptyState}>Race trace unavailable</div>
      )}
    </div>
  );
}

export default ReplayRaceTrace;
